import HelpIcon from '@mui/icons-material/Help'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Box, Divider, Tooltip, Typography } from '@mui/material'
import {
  DashboardMenuItem,
  MenuItemLink,
  MenuProps,
  useGetIdentity,
  usePermissions,
  useSidebarState,
  useTranslate,
} from 'react-admin'
import clinicTeam from '../clinic-team'
import emrs from '../emrs'
import groups from '../groups'
import leads from '../leads'
import owners from '../owners'
import users from '../users'
import vetShares from '../vet-shares'

/**
 * Small uppercase section label between menu groups. Jun 06/26 issue list
 * (V2): new clinics found the flat sidebar confusing — "don't understand the
 * meaning of the extra menu options (my groups vs owners, pet shares)".
 * Grouping by function + descriptive hover tooltips on the two
 * collaboration items answers that without moving anything.
 */
const MenuSection = ({ label }: { label: string }) => {
  // The sidebar defaults to the mini (closed) variant, where there is no
  // room for text — labels clip to "PATIEN…". Render a plain divider there
  // and the full label only when the sidebar is open.
  const [sidebarOpen] = useSidebarState()
  if (!sidebarOpen) {
    return <Divider sx={{ my: 1 }} />
  }
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        px: 2,
        pt: 2,
        pb: 0.5,
        color: 'text.secondary',
        fontWeight: 600,
        letterSpacing: 0.8,
        textTransform: 'uppercase',
      }}
    >
      {label}
    </Typography>
  )
}

const Menu = ({ dense = false }: MenuProps) => {
  const translate = useTranslate()
  const { permissions } = usePermissions()
  const [sidebarOpen] = useSidebarState()

  const { identity } = useGetIdentity()

  const isVet = permissions?.role === 'vet'
  const isVetAdmin = permissions?.role === 'vetadmin'
  const isSiteAdmin = permissions?.role === 'siteadmin'

  // "Clinic Team" is a MASTER-only page: only an account with no parent can
  // create/disable sub-accounts (ada-core createSubAccount + setSubAccountStatus
  // both 403 a sub). Gating on role alone showed the entry to sub-accounts too,
  // who then landed on an empty list with an "Add Sub-Account" button that
  // failed server-side. clinic-team/index.tsx already described this entry as
  // "role-gated to masters"; it wasn't. Read master-ness off
  // identity (same source AssignVetButton and UnassignedPatientBanner use), and
  // fail CLOSED while identity is still loading so the item can never flash in
  // for a sub.
  const isMasterVet = !!identity && (isVet || isVetAdmin) && !identity.masterAccount

  // "Groups" are clinic STAFF teams (members share all clinic pets). The
  // Master/Sub "Clinic Team" page (master manages per-vet sub-accounts) now
  // owns the "Clinic Team" name, so this consent-based feature is relabeled
  // "Care Teams" to avoid two things called Clinic Team.
  const teamsLabel = isSiteAdmin
    ? translate('pos.menu.all_teams_readonly')
    : translate('pos.menu.care_teams')

  return (
    <Box
      sx={{
        marginTop: 1,
        marginBottom: 1,
      }}
    >
      <DashboardMenuItem />

      {/* Patients section. Each item carries a plain-language hover
          description for parity with the Collaboration items below — the
          Jun 13/26 issue was that only half the sidebar explained itself. */}
      <MenuSection label={translate('pos.menu.section_patients')} />
      <Tooltip title={translate('pos.menu.owners_hint')} placement="right" arrow>
        <div>
          <MenuItemLink
            to="/owners"
            state={{ _scrollToTop: true }}
            primaryText={translate(`resources.owners.name`, {
              smart_count: 2,
            })}
            leftIcon={<owners.icon />}
            dense={dense}
          />
        </div>
      </Tooltip>
      {/* "Add New Client" intentionally has no sidebar entry — it was
          redundant with the "Add New Client" button on the Clients list, which
          navigates to the same /register-owner route. */}
      {/* "Notes" = clinical records / EMRs — vague on its own, so spell it
          out in the hover. */}
      <Tooltip title={translate('pos.menu.notes_hint')} placement="right" arrow>
        <div>
          <MenuItemLink
            to="/emrs"
            state={{ _scrollToTop: true }}
            primaryText={translate(`resources.emrs.name`, {
              smart_count: 2,
            })}
            leftIcon={<emrs.icon />}
            dense={dense}
          />
        </div>
      </Tooltip>

      {/* Collaboration section: vet-to-vet pet sharing + clinic staff teams.
          These two are the items new clinics reported not understanding —
          each carries a plain-language hover description. */}
      {(isVet || isVetAdmin || isSiteAdmin) && (
        <MenuSection label={translate('pos.menu.section_sharing')} />
      )}
      {isVet && (
        <Tooltip title={translate('pos.menu.pet_shares_hint')} placement="right" arrow>
          <div>
            <MenuItemLink
              to="/vet-shares"
              state={{ _scrollToTop: true }}
              primaryText={translate('pos.pet_shares.title')}
              leftIcon={<vetShares.icon />}
              dense={dense}
            />
          </div>
        </Tooltip>
      )}
      {isMasterVet && (
        <Tooltip title={translate('pos.menu.clinic_team_hint')} placement="right" arrow>
          <div>
            <MenuItemLink
              to="/clinic-team"
              state={{ _scrollToTop: true }}
              primaryText={translate('pos.menu.clinic_team')}
              leftIcon={<clinicTeam.icon />}
              dense={dense}
            />
          </div>
        </Tooltip>
      )}
      {(isVet || isVetAdmin || isSiteAdmin) && (
        <Tooltip title={translate('pos.menu.care_teams_hint')} placement="right" arrow>
          <div>
            <MenuItemLink
              to="/groups"
              state={{ _scrollToTop: true }}
              primaryText={teamsLabel}
              leftIcon={<groups.icon />}
              dense={dense}
            />
          </div>
        </Tooltip>
      )}

      {/* Site administration - siteadmin only */}
      {isSiteAdmin && (
        <>
          <MenuSection label={translate('pos.menu.section_admin')} />
          <MenuItemLink
            to="/leads"
            state={{ _scrollToTop: true }}
            primaryText={translate(`resources.leads.name`, {
              smart_count: 2,
            })}
            leftIcon={<leads.icon />}
            dense={dense}
          />
          <MenuItemLink
            to="/users"
            state={{ _scrollToTop: true }}
            primaryText={translate('pos.menu.manage_users')}
            leftIcon={<users.icon />}
            dense={dense}
          />
        </>
      )}

      {/* Help menu item at the bottom */}
      <MenuItemLink
        to="/help"
        state={{ _scrollToTop: true }}
        primaryText={translate('pos.help.menu_title')}
        leftIcon={<HelpIcon />}
        dense={dense}
        sx={{
          mt: 2,
          pt: 1,
          // borderTopColor, not borderColor: the shorthand also recolored the
          // 3px left active-indicator border, so Help always showed a gray
          // "selected" bar in the collapsed sidebar.
          borderTop: 1,
          borderTopColor: 'divider',
        }}
      />
      {/* A plain link, not a MenuItemLink (it leaves the SPA), so it has to
          honor the mini sidebar itself — it used to print its label anyway,
          leaving a clipped "M" under the icons on every page. */}
      <Tooltip title={sidebarOpen ? '' : translate('pos.menu.my_account')} placement="right" arrow>
        <Box
          component="a"
          href="https://www.adapetmedical.com/account/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label={translate('pos.menu.my_account')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            pl: '19px',
            pr: 2,
            py: 1,
            mt: 0.5,
            fontSize: '0.875rem',
            color: 'text.secondary',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            '&:hover': { color: 'primary.main' },
          }}
        >
          <OpenInNewIcon sx={{ fontSize: 20, flexShrink: 0 }} />
          {sidebarOpen && translate('pos.menu.my_account')}
        </Box>
      </Tooltip>
    </Box>
  )
}

export default Menu
